import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

/*
 * TtkTiles · Habit Tiles as a phygital HUD.
 * The section reads as an augmented-reality view of a room where physical
 * Habit Tiles are placed. Only the hero image + HUD chrome remain. All
 * captions and the tile-tap widget below have been removed as they were
 * redundant once the AR view exists.
 */

const StatusChip = ({ children, className = "" }) => (
  <div
    className={`inline-flex items-center gap-2 rounded-full border border-signal/50 bg-ink/70 px-3 py-1 font-mono text-[9px] tracking-[0.22em] text-signal backdrop-blur-md ${className}`}
  >
    <span className="relative flex h-1.5 w-1.5">
      <span className="absolute inset-0 animate-ping rounded-full bg-signal/70" />
      <span className="relative inline-block h-1.5 w-1.5 rounded-full bg-signal" />
    </span>
    {children}
  </div>
);

const now = () =>
  new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

const TtkTiles = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section
      ref={ref}
      data-testid="ttk-tiles-section"
      className="relative overflow-hidden px-6 py-24 md:px-12 md:py-32"
    >
      <div className="mx-auto max-w-[1600px]">
        {/* Eyebrow · headline */}
        <div className="max-w-2xl">
          <p className="font-mono text-[11px] tracking-[0.28em] text-ash">
            05 · THE HABIT TILES
          </p>
          <h2
            data-testid="ttk-tiles-headline"
            className="mt-4 font-display text-3xl font-light leading-[1.05] text-white md:text-5xl lg:text-6xl"
          >
            Engineer your space for{" "}
            <span className="text-signal">success</span>.
          </h2>
          <p className="mt-6 max-w-xl text-sm leading-relaxed text-fog md:text-base">
            Habit Tiles are small, phone-tappable ceramic squares. This is
            environmental design for behaviour. You place them where the habit
            actually lives, on the bathroom mirror, above the dresser, next
            to the bed. A tap of your phone against the tile and the moment
            lands in your twin. No unlocking, no app-hunting, no fifth
            reminder to log the vitamin you just took.
          </p>
        </div>

        {/* HUD hero image · phygital view of the room                    */}
        <div
          data-testid="ttk-tiles-hud"
          className="relative mt-10 overflow-hidden rounded-xl border border-signal/25 bg-ink-2/40"
        >
          <picture>
            <source
              media="(max-width: 767px)"
              srcSet="/assets/tiles_hud_bedroom_mobile.jpg"
            />
            <motion.img
              src="/assets/tiles_hud_bedroom.jpg"
              alt="Trifit Habit Tiles placed around a modern bedroom, tagged with a neon-green HUD"
              style={{ y: bgY }}
              className="block h-[560px] w-full object-cover md:h-[380px] lg:h-[440px]"
            />
          </picture>
          {/* Scan-line + vignette wash so it reads as an AR viewport */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "repeating-linear-gradient(0deg, rgba(0,255,128,0.045) 0 1px, rgba(0,0,0,0) 1px 3px), radial-gradient(120% 80% at 50% 50%, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 100%)",
              mixBlendMode: "screen",
            }}
            aria-hidden="true"
          />

          {/* Top-left HUD info chip */}
          <div className="absolute left-4 top-4 flex flex-wrap gap-2 md:left-6 md:top-6">
            <StatusChip>LIVE · PHYGITAL VIEW</StatusChip>
            <StatusChip className="hidden md:inline-flex">
              5 TILES · 1 BAND · 1 KIT
            </StatusChip>
          </div>

          {/* Bottom-right HUD reticle + timestamp */}
          <div className="absolute bottom-4 right-4 flex items-center gap-2 md:bottom-6 md:right-6">
            <span className="font-mono text-[9px] tracking-[0.24em] text-signal">
              REC · {now()}
            </span>
            <span className="relative flex h-2 w-2">
              <span className="absolute inset-0 animate-ping rounded-full bg-signal/70" />
              <span className="relative inline-block h-2 w-2 rounded-full bg-signal" />
            </span>
          </div>

          {/* Corner sight brackets (frame the frame) */}
          {[
            "top-3 left-3 border-t-2 border-l-2",
            "top-3 right-3 border-t-2 border-r-2",
            "bottom-3 left-3 border-b-2 border-l-2",
            "bottom-3 right-3 border-b-2 border-r-2",
          ].map((cls) => (
            <span
              key={cls}
              aria-hidden="true"
              className={`pointer-events-none absolute h-6 w-6 border-signal/70 ${cls}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TtkTiles;
